import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

const API = process.env.REACT_APP_BACKEND_URL || '';

const TEMPLATE_VARIABLES = [
  {
    category: 'Flight Information',
    variables: [
      { name: 'flight_number', description: 'Flight number (e.g. TB1711, QS2472)' },
      { name: 'flight_date', description: 'Flight date (e.g. 21/03/2026)' },
      { name: 'flight_time', description: 'Flight departure/arrival time (e.g. 14:30)' },
      { name: 'departure', description: 'Departure airport code (e.g. BRU)' },
      { name: 'arrival', description: 'Arrival airport code (e.g. ACE)' },
      { name: 'passenger_count', description: 'Number of passengers on the flight' },
      { name: 'flight_count', description: 'Total number of flights (used in invoices)' },
    ]
  },
  {
    category: 'Agency & Company',
    variables: [
      { name: 'agency_name', description: 'Name of the agency (e.g. Air Transport Trade SRL)' },
      { name: 'agency_email', description: 'Email address of the agency' },
      { name: 'contact_person', description: 'Agency contact person name' },
      { name: 'company_name', description: 'Your company name (from company settings)' },
      { name: 'company_email', description: 'Your company email address' },
      { name: 'company_phone', description: 'Your company phone number' },
      { name: 'company_vat', description: 'Your company VAT number' },
      { name: 'company_website', description: 'Your company website URL' },
    ]
  },
  {
    category: 'Invoice & Payment',
    variables: [
      { name: 'invoice_number', description: 'Invoice number (e.g. 2026010001)' },
      { name: 'invoice_date', description: 'Date the invoice was created' },
      { name: 'total_amount', description: 'Total invoice amount in euros' },
      { name: 'due_date', description: 'Payment due date' },
      { name: 'outstanding_amount', description: 'Outstanding balance amount' },
      { name: 'amount', description: 'Payment amount (used in payment notifications)' },
      { name: 'payment_method', description: 'Payment method (Bank Transfer, QR Code, etc.)' },
      { name: 'period', description: 'Statement period (e.g. January 2026)' },
    ]
  },
  {
    category: 'User & Approval',
    variables: [
      { name: 'user_name', description: 'Full name of the user performing the action' },
      { name: 'user_email', description: 'Email of the user' },
      { name: 'user_role', description: 'Role of the user (admin, staff, agency)' },
      { name: 'approved_by', description: 'Name of the admin who approved the request' },
    ]
  },
  {
    category: 'Import & Other',
    variables: [
      { name: 'count', description: 'Number of items imported (used in flight import)' },
      { name: 'filename', description: 'Name of the imported file' },
      { name: 'verification_code', description: 'Verification code for email verification' },
      { name: 'expiry_minutes', description: 'Minutes until verification code expires' },
    ]
  }
];

function VariableReference() {
  const [isOpen, setIsOpen] = React.useState(true);

  return (
    <div style={{
      background: '#2A2E39',
      border: '1px solid #3A3E49',
      borderRadius: '8px',
      marginBottom: '24px',
      overflow: 'hidden'
    }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        data-testid="variable-reference-toggle"
        style={{
          width: '100%',
          padding: '14px 20px',
          background: 'transparent',
          border: 'none',
          color: '#FFD700',
          fontSize: '16px',
          fontWeight: 'bold',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          textAlign: 'left'
        }}
      >
        <span>Template Variables Reference</span>
        <span style={{ fontSize: '12px', color: '#999', transition: 'transform 0.2s', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>
          ▼
        </span>
      </button>

      {isOpen && (
        <div style={{ padding: '0 20px 20px' }}>
          <p style={{ color: '#999', fontSize: '13px', marginBottom: '16px', marginTop: 0 }}>
            Use these variables in your email subject and body. They will be automatically replaced with actual values when the email is sent.
            Copy and paste the variable name including the curly braces.
          </p>

          <div style={{ display: 'grid', gap: '16px' }}>
            {TEMPLATE_VARIABLES.map((group) => (
              <div key={group.category}>
                <h4 style={{ color: '#4A9FFF', margin: '0 0 8px 0', fontSize: '14px', borderBottom: '1px solid #3A3E49', paddingBottom: '6px' }}>
                  {group.category}
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '4px' }}>
                  {group.variables.map((v) => (
                    <div key={v.name} style={{ display: 'flex', alignItems: 'baseline', gap: '10px', padding: '4px 0' }}>
                      <code
                        style={{
                          background: '#1A1E29',
                          color: '#FFD700',
                          padding: '2px 8px',
                          borderRadius: '3px',
                          fontSize: '13px',
                          fontFamily: 'monospace',
                          whiteSpace: 'nowrap',
                          flexShrink: 0,
                          cursor: 'pointer',
                          border: '1px solid #3A3E49'
                        }}
                        title="Click to copy"
                        onClick={() => {
                          navigator.clipboard.writeText(`{${v.name}}`);
                          // Brief visual feedback via title
                        }}
                      >
                        {`{${v.name}}`}
                      </code>
                      <span style={{ color: '#CCC', fontSize: '13px' }}>{v.description}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function EmailTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [testingTemplate, setTestingTemplate] = useState(null);
  const [testEmail, setTestEmail] = useState('');

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/email-templates-extended`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setTemplates(response.data || []);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      toast.error('Failed to load email templates');
    } finally {
      setLoading(false);
    }
  };

  const handleScanTemplates = async () => {
    if (!window.confirm('This will scan the codebase and add any missing email templates. Continue?')) {
      return;
    }

    setScanning(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/email-templates/scan`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success(response.data.message);
        await fetchTemplates();
      }
    } catch (error) {
      console.error('Scan failed:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Scan failed';
      toast.error(errorMsg);
    } finally {
      setScanning(false);
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate({ ...template });
  };

  const handlePreview = (template) => {
    setPreviewTemplate(template);
  };

  const handleTest = (template) => {
    setTestingTemplate(template);
    setTestEmail('');
  };

  const handleSendTest = async () => {
    if (!testEmail) {
      toast.error('Please enter a test email address');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/api/email-templates/${testingTemplate.template_key}/test`,
        {
          test_email: testEmail,
          sample_data: {} // Using default sample data from backend
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(response.data.message, { autoClose: 5000 });
        
        // Show which SMTP config was used
        if (response.data.smtp_config_used) {
          const smtpInfo = response.data.smtp_config_used;
          toast.info(
            `Sent via: ${smtpInfo.email} (${smtpInfo.smtp_server}:${smtpInfo.smtp_port})`,
            { autoClose: 8000 }
          );
        }
        
        setTestingTemplate(null);
        setTestEmail('');
      }
    } catch (error) {
      console.error('Test email failed:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to send test email';
      toast.error(errorMsg, { autoClose: 8000 });
    }
  };

  const handleSave = async () => {
    if (!editingTemplate) return;

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/api/email-templates-extended/${editingTemplate.template_key}`,
        editingTemplate,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Template updated successfully');
      setEditingTemplate(null);
      await fetchTemplates();
    } catch (error) {
      console.error('Failed to save template:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to save template';
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditingTemplate(null);
  };

  const handleChange = (field, value) => {
    setEditingTemplate(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const formatTemplateKey = (key) => {
    return key
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#FFFFFF' }}>
        <h2>Loading Email Templates...</h2>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', color: '#FFFFFF', maxWidth: '1400px', margin: '0 auto' }}>
      <div style={{ marginBottom: '30px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ fontSize: '28px', color: '#FFD700' }}>
          📧 Email Templates Management
        </h1>
        
        <button
          onClick={handleScanTemplates}
          disabled={scanning}
          style={{
            padding: '10px 20px',
            background: '#4A9FFF',
            color: '#FFFFFF',
            border: 'none',
            borderRadius: '4px',
            cursor: scanning ? 'not-allowed' : 'pointer',
            fontWeight: 'bold'
          }}
        >
          {scanning ? 'Scanning...' : '🔍 Scan & Update Templates'}
        </button>
      </div>

      <div style={{
        background: '#3A3E49',
        border: '1px solid #4A9FFF',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '24px'
      }}>
        <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.6' }}>
          <strong>ℹ️ Information:</strong> Email templates are automatically detected from the codebase. 
          You can customize the subject, body, choose sender configuration, and specify recipients. 
          Use variables like {'{flight_number}'}, {'{agency_name}'}, etc. in your templates.
        </p>
      </div>

      <VariableReference />

      {templates.length === 0 ? (
        <div style={{
          background: '#2A2E39',
          border: '1px solid #3A3E49',
          borderRadius: '8px',
          padding: '40px',
          textAlign: 'center'
        }}>
          <p style={{ fontSize: '18px', marginBottom: '20px' }}>
            No email templates found. Click "Scan & Update Templates" to detect templates from the codebase.
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '20px' }}>
          {templates.map((template) => (
            <div
              key={template.template_key}
              style={{
                background: '#2A2E39',
                border: editingTemplate?.template_key === template.template_key ? '2px solid #4A9FFF' : '1px solid #3A3E49',
                borderRadius: '8px',
                padding: '24px'
              }}
            >
              {editingTemplate?.template_key === template.template_key ? (
                // Edit Mode
                <div>
                  <h3 style={{ color: '#FFD700', marginBottom: '20px', fontSize: '20px' }}>
                    Editing: {formatTemplateKey(template.template_key)}
                  </h3>

                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', color: '#FFFFFF', fontWeight: 'bold' }}>
                      Sender Email Configuration
                    </label>
                    <select
                      value={editingTemplate.sender_override || ''}
                      onChange={(e) => handleChange('sender_override', e.target.value || null)}
                      style={{
                        width: '100%',
                        padding: '10px',
                        background: '#1A1E29',
                        border: '1px solid #3A3E49',
                        borderRadius: '4px',
                        color: '#FFFFFF',
                        fontSize: '14px'
                      }}
                    >
                      <option value="">Auto (Based on usage rules)</option>
                      <option value="contact">Contact (contact@viphandling.com)</option>
                      <option value="accounting">Accounting (accounting@viphandling.com)</option>
                      <option value="admin">Admin (admin@viphandling.com)</option>
                    </select>
                  </div>

                  <div style={{ marginBottom: '16px', display: 'flex', gap: '20px' }}>
                    <label style={{ color: '#FFFFFF', display: 'flex', alignItems: 'center' }}>
                      <input
                        type="checkbox"
                        checked={editingTemplate.send_to_company}
                        onChange={(e) => handleChange('send_to_company', e.target.checked)}
                        style={{ marginRight: '8px', width: '18px', height: '18px' }}
                      />
                      <strong>Send to Our Company</strong>
                    </label>

                    <label style={{ color: '#FFFFFF', display: 'flex', alignItems: 'center' }}>
                      <input
                        type="checkbox"
                        checked={editingTemplate.send_to_agency}
                        onChange={(e) => handleChange('send_to_agency', e.target.checked)}
                        style={{ marginRight: '8px', width: '18px', height: '18px' }}
                      />
                      <strong>Send to Agency</strong>
                    </label>
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', color: '#FFFFFF', fontWeight: 'bold' }}>
                      Email Subject
                    </label>
                    <input
                      type="text"
                      value={editingTemplate.subject}
                      onChange={(e) => handleChange('subject', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '10px',
                        background: '#1A1E29',
                        border: '1px solid #3A3E49',
                        borderRadius: '4px',
                        color: '#FFFFFF',
                        fontSize: '14px'
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', color: '#FFFFFF', fontWeight: 'bold' }}>
                      Email Body (HTML supported)
                    </label>
                    <textarea
                      value={editingTemplate.body}
                      onChange={(e) => handleChange('body', e.target.value)}
                      rows={8}
                      style={{
                        width: '100%',
                        padding: '10px',
                        background: '#1A1E29',
                        border: '1px solid #3A3E49',
                        borderRadius: '4px',
                        color: '#FFFFFF',
                        fontSize: '14px',
                        fontFamily: 'monospace',
                        resize: 'vertical'
                      }}
                    />
                  </div>

                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      style={{
                        padding: '10px 20px',
                        background: '#28A745',
                        color: '#FFFFFF',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: saving ? 'not-allowed' : 'pointer',
                        fontWeight: 'bold'
                      }}
                    >
                      {saving ? 'Saving...' : '💾 Save Template'}
                    </button>

                    <button
                      onClick={handleCancel}
                      style={{
                        padding: '10px 20px',
                        background: '#6C757D',
                        color: '#FFFFFF',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontWeight: 'bold'
                      }}
                    >
                      ✖ Cancel
                    </button>
                  </div>
                </div>
              ) : (
                // View Mode
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <h3 style={{ color: '#FFD700', margin: 0, fontSize: '20px' }}>
                      {formatTemplateKey(template.template_key)}
                    </h3>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => handlePreview(template)}
                        style={{
                          padding: '8px 16px',
                          background: '#17A2B8',
                          color: '#FFFFFF',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontWeight: 'bold',
                          fontSize: '14px'
                        }}
                      >
                        👁️ Preview
                      </button>
                      <button
                        onClick={() => handleTest(template)}
                        style={{
                          padding: '8px 16px',
                          background: '#FFC107',
                          color: '#000000',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontWeight: 'bold',
                          fontSize: '14px'
                        }}
                      >
                        📧 Test
                      </button>
                      <button
                        onClick={() => handleEdit(template)}
                        style={{
                          padding: '8px 16px',
                          background: '#4A9FFF',
                          color: '#FFFFFF',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontWeight: 'bold',
                          fontSize: '14px'
                        }}
                      >
                        ✏️ Edit
                      </button>
                    </div>
                  </div>

                  <div style={{ marginBottom: '12px' }}>
                    <span style={{ color: '#999', fontSize: '14px' }}>Sender: </span>
                    <span style={{ color: '#FFFFFF', fontSize: '14px', fontWeight: 'bold' }}>
                      {template.sender_override ? (
                        `${template.sender_override.charAt(0).toUpperCase() + template.sender_override.slice(1)} (${template.sender_override}@viphandling.com)`
                      ) : (
                        'Auto (Based on usage rules)'
                      )}
                    </span>
                  </div>

                  <div style={{ marginBottom: '12px' }}>
                    <span style={{ color: '#999', fontSize: '14px' }}>Recipients: </span>
                    <span style={{ color: '#FFFFFF', fontSize: '14px' }}>
                      {template.send_to_company && template.send_to_agency ? 'Company & Agency' :
                       template.send_to_company ? 'Company Only' :
                       template.send_to_agency ? 'Agency Only' : 'None'}
                    </span>
                  </div>

                  <div style={{ marginBottom: '12px' }}>
                    <span style={{ color: '#999', fontSize: '14px' }}>Subject: </span>
                    <span style={{ color: '#FFFFFF', fontSize: '14px' }}>{template.subject}</span>
                  </div>

                  <div>
                    <span style={{ color: '#999', fontSize: '14px', display: 'block', marginBottom: '8px' }}>Body Preview:</span>
                    <div style={{
                      background: '#1A1E29',
                      border: '1px solid #3A3E49',
                      borderRadius: '4px',
                      padding: '12px',
                      color: '#FFFFFF',
                      fontSize: '13px',
                      maxHeight: '150px',
                      overflowY: 'auto',
                      whiteSpace: 'pre-wrap',
                      fontFamily: 'monospace'
                    }}>
                      {template.body}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Preview Modal */}
      {previewTemplate && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#2A2E39',
            border: '1px solid #4A9FFF',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '800px',
            maxHeight: '80vh',
            overflow: 'auto',
            width: '90%'
          }}>
            <h2 style={{ color: '#FFD700', marginBottom: '20px' }}>
              Email Preview: {formatTemplateKey(previewTemplate.template_key)}
            </h2>

            <div style={{ marginBottom: '16px' }}>
              <strong style={{ color: '#FFFFFF' }}>Subject:</strong>
              <div style={{
                background: '#1A1E29',
                padding: '10px',
                marginTop: '8px',
                borderRadius: '4px',
                color: '#FFFFFF'
              }}>
                {previewTemplate.subject}
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <strong style={{ color: '#FFFFFF' }}>Body:</strong>
              <div style={{
                background: '#1A1E29',
                padding: '16px',
                marginTop: '8px',
                borderRadius: '4px',
                color: '#FFFFFF',
                maxHeight: '400px',
                overflow: 'auto'
              }} dangerouslySetInnerHTML={{ __html: previewTemplate.body }}>
              </div>
            </div>

            <button
              onClick={() => setPreviewTemplate(null)}
              style={{
                padding: '10px 20px',
                background: '#6C757D',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Test Email Modal */}
      {testingTemplate && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#2A2E39',
            border: '1px solid #4A9FFF',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h2 style={{ color: '#FFD700', marginBottom: '20px' }}>
              Send Test Email
            </h2>

            <p style={{ color: '#FFFFFF', marginBottom: '16px' }}>
              Template: <strong>{formatTemplateKey(testingTemplate.template_key)}</strong>
            </p>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', color: '#FFFFFF', fontWeight: 'bold' }}>
                Test Email Address
              </label>
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="Enter email address"
                style={{
                  width: '100%',
                  padding: '10px',
                  background: '#1A1E29',
                  border: '1px solid #3A3E49',
                  borderRadius: '4px',
                  color: '#FFFFFF',
                  fontSize: '14px'
                }}
              />
            </div>

            <p style={{ color: '#999', fontSize: '13px', marginBottom: '20px' }}>
              Note: The email will be sent with sample data for all template variables.
            </p>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={handleSendTest}
                disabled={!testEmail}
                style={{
                  padding: '10px 20px',
                  background: '#28A745',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: !testEmail ? 'not-allowed' : 'pointer',
                  fontWeight: 'bold',
                  opacity: !testEmail ? 0.5 : 1
                }}
              >
                📧 Send Test Email
              </button>

              <button
                onClick={() => {
                  setTestingTemplate(null);
                  setTestEmail('');
                }}
                style={{
                  padding: '10px 20px',
                  background: '#6C757D',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EmailTemplates;
